import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { defaultRefetchLists, defaultRetry } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import {
    DeleteWebhookRequest,
    GetWebhookRequest,
    GetWebhookSecretRequest,
    ListWebhooksRequest,
    RotateWebhookSecretRequest,
    type Webhook,
} from "gitpod-next-api/gitpod/v1/webhook_pb";

export const webhookQueryKey = {
    list: () => keyWithPrincipal(["webhooks", "list"]),
    get: (webhookId?: string) => keyWithPrincipal(["webhooks", { webhookId }]),
    secret: (webhookId?: string) => keyWithPrincipal(["webhooks", "secret", { webhookId }]),
};

export type PlainWebhook = PlainMessage<Webhook>;

export const useListWebhooks = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: webhookQueryKey.list(),
        queryFn: async (): Promise<PlainWebhook[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = new ListWebhooksRequest();
            const webhooks = await autoPaginate(
                (params) => api.webhookService.listWebhooks(params),
                request,
                (response) => response.webhooks,
            );
            return webhooks.map((w) => toPlainMessage(w));
        },
        throwOnError: false,
        retry: defaultRetry,
        enabled: !!user,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useGetWebhook = (webhookId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: webhookQueryKey.get(webhookId),
        queryFn: async (): Promise<PlainWebhook> => {
            if (!webhookId) {
                throw new Error("webhookId is required");
            }
            const response = await api.webhookService.getWebhook(new GetWebhookRequest({ webhookId }));
            if (!response.webhook) {
                throw new Error("Webhook not found");
            }
            return toPlainMessage(response.webhook);
        },
        enabled: !!webhookId,
        retry: defaultRetry,
    });
};

export const useGetWebhookSecret = (webhookId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: webhookQueryKey.secret(webhookId),
        queryFn: async (): Promise<string> => {
            if (!webhookId) {
                throw new Error("webhookId is required");
            }
            const response = await api.webhookService.getWebhookSecret(new GetWebhookSecretRequest({ webhookId }));
            return response.secret;
        },
        enabled: !!webhookId,
        retry: defaultRetry,
    });
};

export const useRotateWebhookSecret = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (webhookId: string): Promise<string> => {
            const response = await api.webhookService.rotateWebhookSecret(
                new RotateWebhookSecretRequest({ webhookId }),
            );
            return response.secret;
        },
        onSuccess: (newSecret, webhookId) => {
            queryClient.setQueryData(webhookQueryKey.secret(webhookId), newSecret);
        },
    });
};

export const useDeleteWebhook = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (webhookId: string): Promise<void> => {
            await api.webhookService.deleteWebhook(new DeleteWebhookRequest({ webhookId }));
        },
        onSuccess: (_, webhookId) => {
            // Remove from list cache
            queryClient.setQueryData<PlainWebhook[]>(webhookQueryKey.list(), (old) =>
                old?.filter((w) => w.id !== webhookId),
            );
            // Remove individual webhook cache
            queryClient.removeQueries({ queryKey: webhookQueryKey.get(webhookId) });
            // Remove secret cache
            queryClient.removeQueries({ queryKey: webhookQueryKey.secret(webhookId) });
        },
    });
};

export const useCanDeleteWebhook = (webhookId: string | undefined) => {
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();
    const canDeleteWebhook = webhookId ? hasPermission(ResourceType.WEBHOOK, webhookId, "webhook:delete") : false;
    return {
        canDeleteWebhook,
        isLoading: isLoadingPermissions,
    };
};
